import { Component, input, output } from '@angular/core';
import { provideTranslocoScope, TranslocoDirective } from '@jsverse/transloco';
import { HlmEmptyImports } from '@spartan-ng/helm/empty';
import { HlmSkeletonImports } from '@spartan-ng/helm/skeleton';
import { ManualHtmlPipe } from './manual-html.pipe';
import { ManualVideosDirective } from './manual-videos.directive';
import { isIntroSection, type ManualChapter } from './manual.graphql';

/** An internal manual link found inside chapter HTML: `/manual/<chapterId>[#<sectionId>]`. */
export interface ManualChapterLink {
  chapterId: string;
  sectionId: string | null;
}

const MANUAL_LINK_PREFIX = '/manual/';

/**
 * Sections of one manual chapter, shared by the `/manual` page and the help panel. A `null`
 * chapter (refused or unknown) renders the not-found state and nothing else; the host owns the
 * chapter title. A click on an internal `/manual/...` link does not reload the document: it is
 * emitted as `chapterLink` and the host decides how to open it.
 */
@Component({
  selector: 'au-manual-chapter-content',
  imports: [
    TranslocoDirective,
    HlmEmptyImports,
    HlmSkeletonImports,
    ManualHtmlPipe,
    ManualVideosDirective,
  ],
  providers: [provideTranslocoScope('manual')],
  template: `
    <ng-container *transloco="let t; scope: 'manual'">
      @if (loading()) {
        <div class="space-y-3">
          <hlm-skeleton class="h-6 w-48" />
          <hlm-skeleton class="h-4 w-full" />
          <hlm-skeleton class="h-4 w-3/4" />
        </div>
      } @else if (chapter(); as chapter) {
        <div
          class="space-y-6"
          (click)="onContentClick($event)"
        >
          @for (section of chapter.sections; track section.id) {
            <section
              class="scroll-mt-4"
              [id]="section.id"
            >
              @if (!isIntro(chapter, section)) {
                <h3 class="mb-2 text-lg font-semibold tracking-tight">{{ section.title }}</h3>
              }
              <div
                class="[&_code]:bg-muted [&_th]:bg-muted/50 text-sm leading-relaxed [&_a]:underline [&_code]:rounded [&_code]:px-1 [&_img]:max-w-full [&_img]:rounded-md [&_li]:my-1 [&_ol]:list-decimal [&_ol]:ps-5 [&_p]:my-2 [&_table]:my-4 [&_table]:w-full [&_table]:border-separate [&_table]:border-spacing-0 [&_table]:overflow-hidden [&_table]:rounded-lg [&_table]:border [&_td]:px-3 [&_td]:py-2 [&_td]:align-top [&_th]:border-b [&_th]:px-3 [&_th]:py-2 [&_th]:text-start [&_th]:align-middle [&_th]:font-medium [&_tr:not(:last-child)_td]:border-b [&_ul]:list-disc [&_ul]:ps-5 [&_video]:max-w-full [&_video]:rounded-md"
                [innerHTML]="section.html | auManualHtml"
                [auManualVideos]="section.html"
              ></div>
            </section>
          }
        </div>
      } @else {
        <hlm-empty data-testid="manual-not-found">
          <hlm-empty-header>
            <h3 hlmEmptyTitle>{{ t('manual.notFound.Title') }}</h3>
            <p hlmEmptyDescription>{{ t('manual.notFound.Description') }}</p>
          </hlm-empty-header>
        </hlm-empty>
      }
    </ng-container>
  `,
})
export class ManualChapterContent {
  readonly chapter = input<ManualChapter | null>(null);
  readonly loading = input(false);
  readonly chapterLink = output<ManualChapterLink>();

  protected readonly isIntro = isIntroSection;

  protected onContentClick(event: MouseEvent): void {
    if (event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) {
      return;
    }
    const href = (event.target as Element | null)?.closest('a')?.getAttribute('href');
    if (!href?.startsWith(MANUAL_LINK_PREFIX)) return;

    const [path, fragment] = href.split('#');
    const chapterId = decodeURIComponent(path.slice(MANUAL_LINK_PREFIX.length));
    if (!chapterId) return;

    event.preventDefault();
    this.chapterLink.emit({
      chapterId,
      sectionId: fragment ? decodeURIComponent(fragment) : null,
    });
  }
}
